import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetTitle,
    SheetTrigger,
} from '@src/components/ui/sheet';

import { Maximize2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    title: ReactNode;
    value: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    disabled?: boolean;
    renderMap: (
        value: string,
        onChange: (value: string) => void,
        preview: boolean,
    ) => ReactNode;
};

export function MobileMapField({
    title,
    value,
    onChange,
    readOnly,
    disabled,
    renderMap,
}: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value);

    return (
        <Sheet
            open={open}
            onOpenChange={(next) => {
                if (next) setDraft(value);
                setOpen(next);
            }}
        >
            <div className='relative isolate h-[240px] overflow-hidden rounded-xl border border-(--color-border)'>
                <div
                    aria-hidden
                    ref={(node) => {
                        node?.setAttribute('inert', '');
                    }}
                    className='pointer-events-none h-full'
                >
                    {renderMap(value, onChange, true)}
                </div>
                <SheetTrigger asChild>
                    <button
                        type='button'
                        disabled={disabled}
                        className='absolute inset-0 z-[1000] flex items-end justify-center p-4 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-(--color-brand-green) disabled:cursor-not-allowed'
                    >
                        <span className='inline-flex min-h-11 items-center gap-2 rounded-xl bg-(--color-body) px-4 py-2 text-sm font-semibold text-(--color-card-text) shadow-md'>
                            <Maximize2 className='size-4' />
                            {t('translation:expand')}
                        </span>
                    </button>
                </SheetTrigger>
            </div>
            <SheetContent
                side='bottom'
                aria-describedby={undefined}
                overlayClassName='z-[1310] bg-black/45'
                className='font-inter z-[1320] flex h-dvh flex-col gap-0 overflow-hidden rounded-none border-0 bg-(--color-body) p-0 text-(--color-card-text) duration-200! motion-reduce:animate-none'
            >
                <div className='flex shrink-0 items-center justify-between gap-3 px-4 pt-[max(8px,env(safe-area-inset-top))] pb-2'>
                    <SheetTitle className='text-base font-bold'>
                        {title}
                    </SheetTitle>
                    <SheetClose
                        aria-label={t('translation:close')}
                        className='flex size-11 shrink-0 items-center justify-center rounded-full bg-(--color-card) text-(--color-card-subtitle)'
                    >
                        <X className='size-5' />
                    </SheetClose>
                </div>
                <div className='relative isolate min-h-0 flex-1'>
                    {open && renderMap(draft, setDraft, false)}
                </div>
                <div className='flex shrink-0 gap-3 px-4 pt-4 pb-[max(16px,env(safe-area-inset-bottom))]'>
                    <SheetClose className='min-h-[52px] flex-1 rounded-[14px] bg-(--color-bg) px-4 text-sm font-medium text-(--color-card-text-secondary) dark:bg-(--color-bg-v2)'>
                        {t('translation:cancel')}
                    </SheetClose>
                    {!readOnly && (
                        <button
                            type='button'
                            className='min-h-[52px] flex-1 rounded-[14px] bg-(--color-brand-green) px-4 text-base font-medium text-white'
                            onClick={() => {
                                onChange(draft);
                                setOpen(false);
                            }}
                        >
                            {t('translation:apply')}
                        </button>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
